/**
 * PATH: packages/ui/src/kanban/api-fetch/cardAccessLocks.ts
 * FILE_ID: UI-KANBAN-FETCH-CARDLOCKS-001
 * PURPOSE: Call the access-lock lifecycle endpoint from Kanban card UI.
 * WHY: The endpoint is sibling-scoped to Kanban and needs its own auth/base URL seam.
 * FLOW: lock id + justification -> access-lock request -> lifecycle result.
 * DEPENDENCIES: ApiFetchContext; formatDrfError.
 * RELATED: cards.ts; components/KanbanCardLockedFace.tsx.
 */
import type { KanbanApi } from '../api'
import { formatDrfError } from '../formatDrfError'
import type { ApiFetchContext } from './context'

export function createCardAccessLocksApi(ctx: ApiFetchContext) {
  const { baseUrl, getAuthHeaders } = ctx
  return {
    requestAccessUnlock: async (
      lockId: number,
      justification: string,
    ): Promise<{ request_id: number; lock_state: string }> => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
      if (getAuthHeaders) {
        Object.assign(headers, await getAuthHeaders())
      }
      const accessLockBase = baseUrl.replace(/\/kanban\/?$/, '/access-locks')
      const url = `${accessLockBase.replace(/\/+$/, '')}/${lockId}/request-unlock/`
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ justification }),
        credentials: 'include',
      })
      if (!response.ok) {
        let message = `Request access failed (${response.status})`
        try {
          const parsed = formatDrfError(await response.json())
          if (parsed) message = parsed
        } catch {
          // Keep the status-coded error when its body is absent or malformed.
        }
        const error: Error & { status?: number } = new Error(message)
        error.status = response.status
        throw error
      }
      return (await response.json()) as { request_id: number; lock_state: string }
    },
  } satisfies Partial<KanbanApi>
}
